/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Divider as MuiDivider,
  TextField,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  CardContent,
  Modal,
  Container,
  Zoom,
  Card,
  CardHeader,
  CardActions,
  FormControl,
  Select,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { a, useSpring } from "@react-spring/web";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import {
  AccountBox,
  Business,
  BookOnline,
  HourglassTop as HourglassTopIcon,
  LinkOutlined as LinkOutlinedIcon,
  PlaylistAddCheckOutlined as PlaylistAddCheckOutlinedIcon,
  Delete as DeleteIcon,
  Cached as CachedIcon,
  CloseOutlined as CloseOutlinedIcon,
  NotificationsActive as NotificationsActiveIcon,
} from "@mui/icons-material";
import { green, red } from "@mui/material/colors";
import {
  DataProject,
  DataProjectFilterAttribute,
  DataProjectFilterMetadata,
  DataProjectTicketCount,
  DataProjectType,
  getDataProjectTicketCounts,
  getDataProjectFilterMetadata,
  reloadDataProject,
  saveDataProjectCategories,
  saveViewOrder,
  deleteDataProject,
  addDataProject,
} from "../api/dataprojects";
import {
  DataLoaderAdditionalAttribute,
  DataLoaderListOption,
  getDataSystemAdditionalAttributes,
  getAvailableListOptions,
  getReferenceDataSyncSystems,
  StandaloneSyncSystem,
} from "../api/datasystems";
import { AccountNameAutocomplete } from "../components/utils/datasources/AutoComplete";
import { MiraCard } from "../components/mira/components/common/Card";
import { getCrmScoreStyles, NUMBER_FORMATTER } from "../components/utils/utils";
import { ChickenOutModal } from "../components/utils/ChickenOutModal";
import { CirromLogoMin } from "../components/Logo";
import "flag-icons/css/flag-icons.min.css";
import { isEmpty } from "lodash";
import { ProjectAlertsModal } from "../components/data-projects/ProjectAlertsModal";

// Import the modal from the legacy component for use in the new AddNewDataProject
// We need to re-export it or access it from the old module
// For now, we'll create our own version below

const Divider = styled(MuiDivider)(spacing);

const DataProjectCard = styled(MiraCard)`
  cursor: pointer;
  transition: transform 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
  }
`;

const DataProjectCardContent = styled(CardContent)`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;

  &:last-child {
    padding-bottom: 16px;
  }
`;

const HealthScoreContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-around;
  margin: 16px 0;
`;

const HealthScoreBox = styled.div`
  text-align: center;
  flex: 1;
`;

const ScoreBadge = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "scoreValue",
})<{ scoreValue: number }>`
  color: white;
  padding: 8px 32px;
  border-radius: 20px;
  font-weight: bold;
  ${(props) => getCrmScoreStyles(props.scoreValue)}
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
`;

const StatItem = styled.div`
  text-align: center;
  flex: 1;
`;

const getFilterByAttribute = (
  filters: DataProjectFilterAttribute[],
  attribute: string
) => filters.find((filter) => filter.attribute === attribute);

interface DataProjectPanelProps {
  dataProject: DataProject;
  dataProjectTicketCounts: DataProjectTicketCount[];
  onRefresh?: () => void;
  dataProjectType?: DataProjectType;
}

const DataProjectPanel: React.FC<DataProjectPanelProps> = ({
  dataProject,
  dataProjectTicketCounts,
  onRefresh,
  dataProjectType,
}) => {
  const navigate = useNavigate();
  const [clicked, setClicked] = useState<boolean>(false);
  const [deleteProjectModalOpen, setDeleteProjectModalOpen] =
    useState<boolean>(false);
  const [alertsModalOpen, setAlertsModalOpen] = useState<boolean>(false);

  const navigationPath =
    dataProjectType === "monitor"
      ? `/data-project/contact-monitor/${dataProject.id}`
      : dataProjectType === "enrich"
      ? `/data-project/enrich/${dataProject.id}`
      : `/data-project/dashboard/${dataProject.id}`;

  const onClickSpring = useSpring({
    transform: `perspective(600px) rotateX(${clicked ? 360 : 0}deg)`,
    config: { mass: 5, tension: 250, friction: 50 },
    onResolve: () => clicked && navigate(navigationPath),
  });

  const handleCardClick = useCallback(() => {
    setClicked(true);
  }, []);

  const handleDeleteClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setDeleteProjectModalOpen(true);
  }, []);

  const handleAlertsClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setAlertsModalOpen(true);
  }, []);

  const handleAlertsClose = useCallback(() => {
    setAlertsModalOpen(false);
  }, []);

  const handleDelete = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      deleteDataProject(dataProject.id)
        .then(() => {
          onRefresh?.();
        })
        .catch((err) => console.error(err));
    },
    [dataProject.id, onRefresh]
  );

  const scoreLabels =
    dataProjectType === "enrich"
      ? { start: "Matched", latest: "Fully Synced" }
      : { start: "Start", latest: "Latest" };

  const scoreValues =
    dataProjectType === "enrich"
      ? {
          start: dataProject.enrich_matched_percentage || 0,
          latest: dataProject.enrich_fully_synced_percentage || 0,
        }
      : {
          start: dataProject.score_start,
          latest: dataProject.score_latest,
        };

  const projectStats =
    dataProjectType === "enrich"
      ? [
          {
            icon: <LinkOutlinedIcon fontSize="small" color="action" />,
            label: "Linked",
            value: dataProject.enrich_matched_total || 0,
          },
          {
            icon: <HourglassTopIcon fontSize="small" color="action" />,
            label: "Proposed",
            value: dataProject.enrich_proposed_total || 0,
          },
          {
            icon: (
              <PlaylistAddCheckOutlinedIcon fontSize="small" color="action" />
            ),
            label: "Updates",
            value: dataProject.enrich_field_changes || 0,
          },
        ]
      : [
          {
            icon: <AccountBox fontSize="small" color="action" />,
            label: "Contacts",
            value: dataProject.total_contacts || 0,
          },
          {
            icon: <Business fontSize="small" color="action" />,
            label: "Accounts",
            value: dataProject.total_accounts || 0,
          },
          {
            icon: <BookOnline fontSize="small" color="action" />,
            label: "Tickets",
            value:
              dataProjectTicketCounts && dataProjectTicketCounts.length > 0
                ? dataProjectTicketCounts[0].count || 0
                : null,
          },
        ];

  return (
    <a.div style={{ transform: onClickSpring.transform, height: "100%" }}>
      <DataProjectCard onClick={handleCardClick}>
        <DataProjectCardContent>
          {/* Header with flag and name */}
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {dataProject.country_iso && (
              <div
                className={`fi fi-${dataProject.country_iso.toLowerCase()} fis`}
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "10px",
                  border: "1px solid #e2e2e0",
                }}
              />
            )}
            <Tooltip title={dataProject.name}>
              <Typography
                variant="h5"
                style={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {dataProject.name}
              </Typography>
            </Tooltip>
          </div>

          <Divider style={{ margin: "12px 0" }} />

          {/* Health Scores */}
          <HealthScoreContainer>
            <HealthScoreBox>
              <Typography variant="subtitle2" gutterBottom>
                {scoreLabels.start}
              </Typography>
              <ScoreBadge variant="h6" scoreValue={scoreValues.start}>
                {scoreValues.start}%
              </ScoreBadge>
            </HealthScoreBox>
            <HealthScoreBox>
              <Typography variant="subtitle2" gutterBottom>
                {scoreLabels.latest}
              </Typography>
              <ScoreBadge variant="h6" scoreValue={scoreValues.latest}>
                {scoreValues.latest}%
              </ScoreBadge>
            </HealthScoreBox>
          </HealthScoreContainer>

          {/* Stats */}
          <StatsContainer>
            {projectStats.map((stat, index) => (
              <React.Fragment key={stat.label}>
                {index > 0 && <Divider orientation="vertical" flexItem />}
                <StatItem>
                  {stat.icon}
                  <Typography
                    variant="caption"
                    display="block"
                    fontWeight="bold"
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {stat.value === null ? (
                      <CircularProgress
                        style={{ height: "12px", width: "12px" }}
                      />
                    ) : (
                      NUMBER_FORMATTER.format(stat.value)
                    )}
                  </Typography>
                </StatItem>
              </React.Fragment>
            ))}
          </StatsContainer>

          {/* Footer with date and delete */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "12px",
            }}
          >
            {dataProject.first_analysis ? (
              <Typography variant="caption" color="textSecondary">
                Created:{" "}
                {new Date(dataProject.first_analysis).toLocaleDateString()}
              </Typography>
            ) : (
              <Typography
                variant="caption"
                style={{ color: green[900], fontWeight: "bold" }}
              >
                NEW
              </Typography>
            )}
            <div style={{ display: "flex", gap: "4px" }}>
              {(dataProjectType === "data_quality" ||
                dataProjectType === "monitor") && (
                <Tooltip title="Manage alerts" placement="top">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleAlertsClick}
                    aria-label={`Manage alerts for ${dataProject.name}`}
                  >
                    <NotificationsActiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                style={{ color: red[800] }}
                onClick={handleDeleteClick}
                aria-label={`Delete ${dataProject.name} project`}
                title={`Delete ${dataProject.name}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </DataProjectCardContent>
      </DataProjectCard>

      <ChickenOutModal
        modalOpen={deleteProjectModalOpen}
        title="Are you sure you want to delete this data project?"
        message="All the reports previously generated for this project will be deleted"
        onModalOpenChange={setDeleteProjectModalOpen}
        onSuccess={handleDelete}
      />

      <ProjectAlertsModal
        open={alertsModalOpen}
        onClose={handleAlertsClose}
        dataProjectId={dataProject.id}
        dataProjectName={dataProject.name}
      />
    </a.div>
  );
};

// Styled component for the "Add New Project" card
const AddNewCard = styled(MiraCard)`
  cursor: pointer;
  transition: transform 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 3px solid #ffbf23;

  &:hover {
    transform: translateY(-4px);
  }
`;

const AddNewCardContent = styled(CardContent)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 16px;

  &:last-child {
    padding-bottom: 16px;
  }
`;

// TypeScript interfaces for component props
interface NewDataProjectModalProps {
  open: boolean;
  onClose: () => void;
  integratedSystemId: number;
  dataProjectType?: DataProjectType;
  lockedEnrichSystemId?: number;
}

interface AddNewDataProjectProps {
  systemId: number;
  onClose: () => void;
  dataProjectType?: DataProjectType;
  lockedEnrichSystemId?: number;
}

interface ReloadButtonRendererProps {
  data: DataProject;
}

// Consolidated configuration for project type labels
const PROJECT_TYPE_LABELS: Record<
  DataProjectType,
  { modal: string; card: string }
> = {
  monitor: {
    modal: "New Monitor Project",
    card: "Create a new monitor project",
  },
  sync: { modal: "New Sync Project", card: "Create a new sync project" },
  enrich: { modal: "New Enrich Project", card: "Create a new enrich project" },
  data_quality: {
    modal: "New Data Quality Project",
    card: "Create a new data quality project",
  },
};

const getProjectLabel = (
  type: DataProjectType | undefined,
  variant: "modal" | "card"
): string => PROJECT_TYPE_LABELS[type || "data_quality"][variant];

// Modal for creating a new data project
const NewDataProjectModal = React.memo((props: NewDataProjectModalProps) => {
  const [dataProjectName, setDataProjectName] = useState<string>("");
  const [selectedSyncSystemId, setSelectedSyncSystemId] = useState<string>("");
  const [
    selectedAccountAdditionalAttribute,
    setSelectedAccountAdditionalAttribute,
  ] = useState<DataLoaderAdditionalAttribute | null>(null);
  const [listId, setListId] = useState<string>("");
  const [listCondition, setListCondition] = useState<string>("EQUAL");

  const [contactAttribute, setContactAttribute] = useState<string>("");
  const [contactCondition, setContactCondition] = useState<string>("EQUAL");
  const [contactValue, setContactValue] = useState<string>("");

  const [cirromAttribute, setCirromAttribute] = useState<string>("");
  const [cirromAttributeCondition, setCirromAttributeCondition] =
    useState<string>("EQUAL");
  const [cirromAttributeValue, setCirromAttributeValue] = useState<string>("");

  const [accountAttribute, setAccountAttribute] = useState<string>("");
  const [accountAttributeCondition, setAccountAttributeCondition] =
    useState<string>("EQUAL");
  const [accountAttributeValue, setAccountAttributeValue] =
    useState<string>("");

  const [isAddingListFilter, setIsAddingListFilter] = useState<boolean>(false);
  const [isAddingContactFilter, setIsAddingContactFilter] =
    useState<boolean>(false);

  const [isAddingCirromAttributeFilter, setIsAddingCirromAttributeFilter] =
    useState<boolean>(false);
  const [isAccountFilter, setIsAccountFilter] = useState<boolean>(false);

  const [dataLoaderListOptions, setDataLoaderListOptions] = useState<
    DataLoaderListOption[]
  >([]);
  const [standaloneSyncSystems, setStandaloneSyncSystems] = useState<
    StandaloneSyncSystem[]
  >([]);
  const [
    accountAdditionalAttributeOptions,
    setAccountAdditionalAttributeOptions,
  ] = useState<DataLoaderAdditionalAttribute[]>([]);
  const [listOptionsLoading, setListOptionsLoading] = useState<boolean>(false);
  const [filterMetadata, setFilterMetadata] =
    useState<DataProjectFilterMetadata>({
      list_filters: [],
      contact_filters: [],
      cirrom_attribute_filters: [],
      account_filters: [],
    });

  const selectedContactFilter = getFilterByAttribute(
    filterMetadata.contact_filters,
    contactAttribute
  );
  const selectedCirromFilter = getFilterByAttribute(
    filterMetadata.cirrom_attribute_filters,
    cirromAttribute
  );
  const selectedAccountFilter = getFilterByAttribute(
    filterMetadata.account_filters,
    accountAttribute
  );

  useEffect(() => {
    if (props.integratedSystemId && props.open) {
      setDataProjectName("");
      setSelectedSyncSystemId(props.lockedEnrichSystemId?.toString() || "");
      setSelectedAccountAdditionalAttribute(null);
      setListId("");
      setContactAttribute("");
      setContactValue("");
      setCirromAttribute("");
      setCirromAttributeValue("");
      setAccountAttribute("");
      setAccountAttributeValue("");
      setIsAddingListFilter(false);
      setIsAddingContactFilter(false);
      setIsAddingCirromAttributeFilter(false);
      setIsAccountFilter(false);

      if (dataLoaderListOptions.length === 0) {
        setListOptionsLoading(true);
        getAvailableListOptions(null, null, props.integratedSystemId)
          .then((resp) => setDataLoaderListOptions(resp))
          .catch((err) => console.error(err))
          .finally(() => setListOptionsLoading(false));
      }

      if (
        props.dataProjectType === "enrich" &&
        props.lockedEnrichSystemId === undefined
      ) {
        getReferenceDataSyncSystems()
          .then((resp) => setStandaloneSyncSystems(resp))
          .catch((err) => console.error(err));
      }

      if (props.dataProjectType === "enrich") {
        getDataSystemAdditionalAttributes(props.integratedSystemId)
          .then((resp) =>
            setAccountAdditionalAttributeOptions(
              resp.filter((attribute) => attribute.entity === "ACCOUNT")
            )
          )
          .catch((err) => console.error(err));
      }

      getDataProjectFilterMetadata()
        .then((resp) => setFilterMetadata(resp))
        .catch((err) => console.error(err));
    }
  }, [
    props.integratedSystemId,
    props.open,
    props.dataProjectType,
    props.lockedEnrichSystemId,
  ]);

  // Form validation: ensure all active filters have required fields filled
  const isFormValid = useMemo(() => {
    if (!dataProjectName.trim()) return false;
    if (props.dataProjectType === "enrich" && !selectedSyncSystemId)
      return false;
    if (
      props.dataProjectType === "enrich" &&
      selectedAccountAdditionalAttribute === null
    )
      return false;
    if (isAddingListFilter && !listId) return false;
    if (isAddingContactFilter && (!contactAttribute || !contactValue))
      return false;
    if (
      isAddingCirromAttributeFilter &&
      (!cirromAttribute || !cirromAttributeValue)
    )
      return false;
    if (isAccountFilter && (!accountAttribute || !accountAttributeValue))
      return false;
    return true;
  }, [
    dataProjectName,
    props.dataProjectType,
    selectedSyncSystemId,
    selectedAccountAdditionalAttribute,
    isAddingListFilter,
    listId,
    isAddingContactFilter,
    contactAttribute,
    contactValue,
    isAddingCirromAttributeFilter,
    cirromAttribute,
    cirromAttributeValue,
    isAccountFilter,
    accountAttribute,
    accountAttributeValue,
  ]);

  const addNewDataProject = () => {
    let listFilters = [];
    if (isAddingListFilter && listId && listCondition) {
      listFilters = [{ list_id: listId, list_condition: listCondition }];
    }
    let contactFilters = [];
    if (
      isAddingContactFilter &&
      contactAttribute &&
      contactValue &&
      contactCondition
    ) {
      contactFilters = [
        {
          attribute: contactAttribute,
          value: contactValue,
          condition: contactCondition,
        },
      ];
    }
    let cirromAttributeFilters = [];
    if (
      isAddingCirromAttributeFilter &&
      cirromAttribute &&
      cirromAttributeValue &&
      cirromAttributeCondition
    ) {
      cirromAttributeFilters = [
        {
          attribute: cirromAttribute,
          value: cirromAttributeValue,
          condition: cirromAttributeCondition,
        },
      ];
    }

    let accountFilters = [];
    if (
      isAccountFilter &&
      accountAttribute &&
      accountAttributeValue &&
      accountAttributeCondition
    ) {
      accountFilters = [
        {
          attribute: accountAttribute,
          value: accountAttributeValue,
          condition: accountAttributeCondition,
        },
      ];
    }
    addDataProject(
      dataProjectName,
      props.dataProjectType === "enrich" ? [] : listFilters,
      props.dataProjectType === "enrich" ? [] : contactFilters,
      props.dataProjectType === "enrich" ? [] : cirromAttributeFilters,
      accountFilters,
      props.integratedSystemId,
      props.dataProjectType,
      selectedSyncSystemId ? Number.parseInt(selectedSyncSystemId) : undefined,
      selectedAccountAdditionalAttribute || undefined
    )
      .then((response) => {
        props.onClose();
      })
      .catch((err) => console.log(err));
  };

  return (
    <Modal
      open={props.open}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0px",
      }}
      aria-labelledby="new-project-modal-title"
    >
      <Container maxWidth={"sm"}>
        <Zoom in={true}>
          <Card style={{ padding: "10px" }}>
            <CardHeader
              id="new-project-modal-title"
              title={getProjectLabel(props.dataProjectType, "modal")}
              titleTypographyProps={{ variant: "h3" }}
              action={
                <IconButton
                  onClick={() => props.onClose()}
                  aria-label="Close modal"
                >
                  <CloseOutlinedIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent>
              <TextField
                required
                fullWidth
                label="Data Project Name"
                variant="outlined"
                size="small"
                margin="dense"
                value={dataProjectName}
                onChange={(event) => setDataProjectName(event.target.value)}
                data-testid={"data_project_name"}
              />
              {props.dataProjectType === "enrich" && (
                <>
                  {props.lockedEnrichSystemId === undefined && (
                    <TextField
                      select
                      required
                      fullWidth
                      label="Enrich Data Loader System"
                      variant="outlined"
                      size="small"
                      margin="dense"
                      value={selectedSyncSystemId}
                      onChange={(event) =>
                        setSelectedSyncSystemId(event.target.value)
                      }
                    >
                      <MenuItem value="" disabled>
                        Select an enrich system
                      </MenuItem>
                      {standaloneSyncSystems.map((syncSystem) => (
                        <MenuItem key={syncSystem.id} value={syncSystem.id}>
                          {`${syncSystem.name} (${syncSystem.system_type})`}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <TextField
                    select
                    required
                    fullWidth
                    label="CRM Account Additional Attribute"
                    variant="outlined"
                    size="small"
                    margin="dense"
                    value={
                      selectedAccountAdditionalAttribute?.id?.toString() || ""
                    }
                    onChange={(event) => {
                      const nextAttribute =
                        accountAdditionalAttributeOptions.find(
                          (option) =>
                            option.id?.toString() === event.target.value
                        ) || null;
                      setSelectedAccountAdditionalAttribute(nextAttribute);
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select an account additional attribute
                    </MenuItem>
                    {accountAdditionalAttributeOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id?.toString()}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}
              {props.dataProjectType !== "enrich" && (
                <>
                  <div>
                    <Typography variant={"h6"} style={{ fontWeight: "bold" }}>
                      List Filtering
                    </Typography>
                    {!isAddingListFilter && (
                      <div
                        style={{
                          background: "rgba(128, 128, 128, 0.1)",
                          padding: "10px",
                          borderRadius: "5px",
                          marginBottom: "10px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography variant={"caption"}>
                          Filter based on the contacts being in a specific List
                          in the the CRM.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          fullWidth
                          style={{ marginTop: "10px" }}
                          onClick={() => {
                            setIsAddingListFilter(true);
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    )}
                    {isAddingListFilter && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                          padding: "0px 20px",
                          marginBottom: "10px",
                        }}
                      >
                        <Typography
                          variant={"h5"}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          List ID
                        </Typography>
                        <FormControl style={{ minWidth: "75px" }}>
                          <Select
                            size="small"
                            variant="standard"
                            value={listCondition}
                            onChange={(event) =>
                              setListCondition(event.target.value)
                            }
                          >
                            <MenuItem key={"EQUAL"} value={"EQUAL"}>
                              Equals
                            </MenuItem>
                          </Select>
                        </FormControl>
                        {listOptionsLoading && (
                          <CircularProgress
                            size={20}
                            style={{ margin: "8px 16px" }}
                          />
                        )}
                        {!listOptionsLoading &&
                          dataLoaderListOptions.length === 0 && (
                            <TextField
                              required
                              fullWidth
                              label="List Id"
                              variant="standard"
                              size="small"
                              margin="dense"
                              value={listId}
                              onChange={(event) =>
                                setListId(event.target.value)
                              }
                            />
                          )}

                        {!listOptionsLoading &&
                          dataLoaderListOptions.length > 0 && (
                            <Autocomplete
                              fullWidth
                              key={"select-list"}
                              options={dataLoaderListOptions.sort((a, b) =>
                                a.list_name.localeCompare(b.list_name)
                              )}
                              getOptionLabel={(option) =>
                                option.list_name + " [" + option.list_id + "]"
                              }
                              value={
                                (listId &&
                                  dataLoaderListOptions?.filter(
                                    (option) => option.list_id === listId
                                  )[0]) ||
                                null
                              }
                              onChange={(event, value) => {
                                setListId(value?.list_id || "");
                                if (
                                  dataProjectName.length === 0 &&
                                  dataLoaderListOptions.length > 0
                                ) {
                                  setDataProjectName(value?.list_name || "");
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="standard"
                                  size="small"
                                  margin="dense"
                                />
                              )}
                            />
                          )}
                        <IconButton
                          style={{ padding: "0px", color: red[800] }}
                          onClick={() => {
                            setIsAddingListFilter(false);
                            setListId("");
                          }}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </div>
                    )}
                  </div>
                  <Typography variant={"h6"} style={{ fontWeight: "bold" }}>
                    Contact Filtering
                  </Typography>
                  <div>
                    {!isAddingContactFilter && (
                      <div
                        style={{
                          background: "rgba(128, 128, 128, 0.1)",
                          padding: "10px",
                          borderRadius: "5px",
                          marginBottom: "10px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography variant={"caption"}>
                          Filters based various attributes on the contact (e.g
                          Contact City Is London).
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          fullWidth
                          style={{ marginTop: "10px" }}
                          onClick={() => {
                            setIsAddingContactFilter(true);
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    )}
                    {isAddingContactFilter && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: "10px",
                          padding: "0px 20px",
                          marginBottom: "10px",
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel id="contact-attribute-label">
                            Contact Attribute
                          </InputLabel>
                          <Select
                            id="contact-attribute-select"
                            labelId="contact-attribute-label"
                            size="small"
                            variant="standard"
                            value={contactAttribute}
                            onChange={(event) => {
                              const nextAttribute = event.target.value;
                              setContactAttribute(nextAttribute);
                              const nextFilter = getFilterByAttribute(
                                filterMetadata.contact_filters,
                                nextAttribute
                              );
                              setContactCondition(
                                nextFilter?.conditions[0]?.name || "EQUAL"
                              );
                            }}
                          >
                            {filterMetadata.contact_filters.map((filter) => (
                              <MenuItem
                                key={filter.attribute}
                                value={filter.attribute}
                              >
                                {filter.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <Select
                            size="small"
                            variant="standard"
                            value={contactCondition}
                            onChange={(event) =>
                              setContactCondition(event.target.value)
                            }
                          >
                            {(selectedContactFilter?.conditions || []).map(
                              (condition) => (
                                <MenuItem
                                  key={condition.name}
                                  value={condition.name}
                                >
                                  {condition.label}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                        <TextField
                          required
                          fullWidth
                          label="Value"
                          variant="standard"
                          size="small"
                          margin="dense"
                          value={contactValue}
                          onChange={(event) =>
                            setContactValue(event.target.value)
                          }
                        />
                        <IconButton
                          style={{ padding: "0px", color: red[800] }}
                          onClick={() => {
                            setIsAddingContactFilter(false);
                            setContactAttribute("");
                            setContactValue("");
                          }}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </div>
                    )}
                  </div>
                  <div>
                    <Typography variant={"h6"} style={{ fontWeight: "bold" }}>
                      Cirrom Attribute Filtering
                    </Typography>
                    {!isAddingCirromAttributeFilter && (
                      <div
                        style={{
                          background: "rgba(128, 128, 128, 0.1)",
                          padding: "10px",
                          borderRadius: "5px",
                          marginBottom: "10px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography variant={"caption"}>
                          Filter based on attributes Cirrom computes to enhance
                          contacts (e.g Cirrom Created Time Is Last 7 Days).
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          fullWidth
                          style={{ marginTop: "10px" }}
                          onClick={() => {
                            setIsAddingCirromAttributeFilter(true);
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    )}
                    {isAddingCirromAttributeFilter && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: "10px",
                          padding: "0px 20px",
                          marginBottom: "10px",
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel>Cirrom Attribute</InputLabel>
                          <Select
                            size="small"
                            variant="standard"
                            value={cirromAttribute}
                            onChange={(event) => {
                              const nextAttribute = event.target.value;
                              setCirromAttribute(nextAttribute);
                              const nextFilter = getFilterByAttribute(
                                filterMetadata.cirrom_attribute_filters,
                                nextAttribute
                              );
                              setCirromAttributeCondition(
                                nextFilter?.conditions[0]?.name || "EQUAL"
                              );
                            }}
                          >
                            {filterMetadata.cirrom_attribute_filters.map(
                              (filter) => (
                                <MenuItem
                                  key={filter.attribute}
                                  value={filter.attribute}
                                >
                                  {filter.label}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <Select
                            size="small"
                            variant="standard"
                            value={cirromAttributeCondition}
                            onChange={(event) =>
                              setCirromAttributeCondition(event.target.value)
                            }
                            disabled
                          >
                            {(selectedCirromFilter?.conditions || []).map(
                              (condition) => (
                                <MenuItem
                                  key={condition.name}
                                  value={condition.name}
                                >
                                  {condition.label}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                        <TextField
                          required
                          fullWidth
                          label="Value"
                          variant="standard"
                          size="small"
                          margin="dense"
                          value={cirromAttributeValue}
                          onChange={(event) =>
                            setCirromAttributeValue(event.target.value)
                          }
                        />
                        <IconButton
                          style={{ padding: "0px", color: red[800] }}
                          onClick={() => {
                            setIsAddingCirromAttributeFilter(false);
                            setCirromAttribute("");
                            setCirromAttributeValue("");
                          }}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </>
              )}
              <div>
                <Typography variant={"h6"} style={{ fontWeight: "bold" }}>
                  Account Filtering
                </Typography>
              </div>
              {!isAccountFilter && (
                <div
                  style={{
                    background: "rgba(128, 128, 128, 0.1)",
                    padding: "10px",
                    borderRadius: "5px",
                    marginBottom: "10px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant={"caption"}>
                    Filter based on account attributes (e.g Account Name Is
                    Apple).
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    style={{ marginTop: "10px" }}
                    onClick={() => {
                      setIsAccountFilter(true);
                    }}
                  >
                    Add
                  </Button>
                </div>
              )}
              {isAccountFilter && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: "10px",
                    padding: "0px 20px",
                    marginBottom: "10px",
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel>Account Attribute</InputLabel>
                    <Select
                      size="small"
                      variant="standard"
                      value={accountAttribute}
                      onChange={(event) => {
                        const nextAttribute = event.target.value;
                        setAccountAttribute(nextAttribute);
                        const nextFilter = getFilterByAttribute(
                          filterMetadata.account_filters,
                          nextAttribute
                        );
                        setAccountAttributeCondition(
                          nextFilter?.conditions[0]?.name || "EQUAL"
                        );
                      }}
                    >
                      {filterMetadata.account_filters.map((filter) => (
                        <MenuItem
                          key={filter.attribute}
                          value={filter.attribute}
                        >
                          {filter.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <Select
                      size="small"
                      variant="standard"
                      value={accountAttributeCondition}
                      onChange={(event) =>
                        setAccountAttributeCondition(event.target.value)
                      }
                    >
                      {(selectedAccountFilter?.conditions || []).map(
                        (condition) => (
                          <MenuItem key={condition.name} value={condition.name}>
                            {condition.label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                  {accountAttribute === "company" ? (
                    <AccountNameAutocomplete
                      integratedSystemId={props.integratedSystemId}
                      onSelectionChanged={(val) =>
                        setAccountAttributeValue(val.name)
                      }
                      onMultiSelectionChanged={(vals) => {
                        setAccountAttributeValue(
                          vals.map((entry) => entry.name).join("~~")
                        );
                      }}
                      multiple={accountAttributeCondition === "IN"}
                    />
                  ) : (
                    <TextField
                      required
                      fullWidth
                      label="Value"
                      variant="standard"
                      size="small"
                      margin="dense"
                      value={accountAttributeValue}
                      onChange={(event) =>
                        setAccountAttributeValue(event.target.value)
                      }
                    />
                  )}

                  <IconButton
                    style={{ padding: "0px", color: red[800] }}
                    onClick={() => {
                      setIsAccountFilter(false);
                      setAccountAttribute("");
                      setAccountAttributeValue("");
                    }}
                  >
                    <DeleteIcon fontSize={"small"} />
                  </IconButton>
                </div>
              )}

              <Divider />
            </CardContent>
            <CardActions>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row-reverse",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ margin: "2px", padding: "0em 0.5em" }}
                  disabled={!isFormValid}
                  onClick={() => addNewDataProject()}
                >
                  Save
                </Button>
              </div>
            </CardActions>
          </Card>
        </Zoom>
      </Container>
    </Modal>
  );
});

// New AddNewDataProject component with consistent styling
const AddNewDataProject = React.memo((props: AddNewDataProjectProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  return (
    <>
      <div style={{ height: "100%" }}>
        <AddNewCard
          onClick={() => setIsModalOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setIsModalOpen(true);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={getProjectLabel(props.dataProjectType, "card")}
          aria-expanded={isModalOpen}
        >
          <AddNewCardContent>
            <div style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: "10px",
                  marginBottom: "20px",
                }}
              >
                <CirromLogoMin />
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                  {getProjectLabel(props.dataProjectType, "card")}
                </Typography>
              </div>
              <Divider />
            </div>
            <img
              src={"/undraw_new_entries.svg"}
              style={{ maxWidth: "200px", marginTop: "auto" }}
              alt={"add_new_data_project"}
            />
          </AddNewCardContent>
        </AddNewCard>
      </div>
      <NewDataProjectModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          props.onClose();
        }}
        integratedSystemId={props.systemId}
        dataProjectType={props.dataProjectType}
        lockedEnrichSystemId={props.lockedEnrichSystemId}
      />
    </>
  );
});

// Custom cell renderer for the reload button
const ReloadButtonRenderer = (props: ReloadButtonRendererProps) => {
  const handleReload = () => {
    reloadDataProject(props.data.id)
      .then((_) => window.location.reload())
      .catch((err) => console.log(err));
  };

  return (
    <IconButton
      size="small"
      onClick={handleReload}
      disabled={props.data.reloading}
      style={{ padding: "4px" }}
    >
      <CachedIcon fontSize="small" />
    </IconButton>
  );
};

const DraggableProjectList = (props: {
  dataProjects: DataProject[];
  onCancel(): void;
}) => {
  const [rowData, setRowData] = useState<DataProject[]>(props.dataProjects);
  const [modifiedCategories, setModifiedCategories] = useState<{
    [key: number]: string;
  }>({});

  useEffect(() => {
    setRowData(props.dataProjects);
  }, [props.dataProjects]);

  const columnDefs: ColDef<DataProject>[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      rowDrag: true,
      lockPosition: true,
    },
    {
      field: "name",
      headerName: "Project Name",
      flex: 2,
      sortable: false,
    },
    {
      field: "score_latest",
      headerName: "Latest Score",
      flex: 1,
      valueFormatter: (params) => `${params.value}%`,
    },
    {
      field: "total_contacts",
      headerName: "Total Contacts",
      flex: 1,
      valueFormatter: (params) => NUMBER_FORMATTER.format(params.value || 0),
    },
    {
      field: "total_accounts",
      headerName: "Total Accounts",
      flex: 1,
      valueFormatter: (params) => NUMBER_FORMATTER.format(params.value || 0),
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      editable: true,
      onCellValueChanged: (params) => {
        setModifiedCategories((prev) => ({
          ...prev,
          [params.data.id]: params.newValue,
        }));
      },
    },
    {
      field: "first_analysis",
      headerName: "Created On",
      flex: 1,
      valueFormatter: (params) => (params.value ? params.value : "NEW"),
      cellStyle: (params) =>
        !params.value ? { color: green[900], fontWeight: "bold" } : {},
    },
    {
      // @ts-ignore - fabricated field
      field: "reload",
      headerName: "Reload",
      width: 80,
      cellRenderer: ReloadButtonRenderer,
      sortable: false,
      filter: false,
    },
  ];

  const handleSave = () => {
    // Save categories
    const categoriesToUpdate = Object.keys(modifiedCategories).map(
      (projectId) => ({
        id: projectId,
        category: modifiedCategories[projectId],
      })
    );

    if (categoriesToUpdate.length > 0) {
      saveDataProjectCategories(categoriesToUpdate);
    }

    // Save order
    const newOrder = rowData.map((project) => project.id);
    saveViewOrder(newOrder)
      .then((_) => window.location.reload())
      .catch((err) => console.log(err));
  };

  return (
    <div>
      <div
        style={{
          height: `${Math.max(props.dataProjects.length * 42 + 50, 300)}px`,
          width: "100%",
        }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          rowDragManaged={true}
          onRowDragEnd={(event) => {
            const newRowData = [];
            event.api.forEachNode((node) => newRowData.push(node.data));
            setRowData(newRowData);
          }}
          defaultColDef={{
            resizable: true,
          }}
          suppressMoveWhenRowDragging={true}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginTop: "10px",
          gap: "10px",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{ textTransform: "capitalize" }}
          onClick={handleSave}
        >
          Save
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          style={{ textTransform: "capitalize" }}
          onClick={props.onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

interface AllDataProjectsProps {
  dataProjects: DataProject[];
  fetchDataProjects: () => void;
  setSelectedSystemId: (number) => void;
  dataProjectType?: DataProjectType;
}

export const AllDataProjects = React.memo((props: AllDataProjectsProps) => {
  const { integratedSystemId, enrichSystemId } = useParams<{
    integratedSystemId?: string;
    enrichSystemId?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [eligibleDataProjects, setEligibleDataProjects] = useState<
    DataProject[]
  >([]);
  const [dataProjectTicketCounts, setDataProjectTicketCounts] = useState<
    DataProjectTicketCount[]
  >([]);
  const [availableProjectCategories, setAvailableProjectCategories] = useState<
    string[]
  >([]);
  const [selectedProjectCategory, setSelectedProjectCategory] =
    useState<string>();
  const [showEditView, setShowEditView] = useState<boolean>(false);
  const parsedEnrichSystemId = enrichSystemId
    ? Number.parseInt(enrichSystemId, 10)
    : undefined;
  const selectedEnrichSystemName = useMemo(
    () =>
      parsedEnrichSystemId
        ? props.dataProjects.find(
            (project) => project.enrich_system_id === parsedEnrichSystemId
          )?.enrich_system_name
        : undefined,
    [parsedEnrichSystemId, props.dataProjects]
  );

  // Main effect: Filter and categorize projects when system changes
  useEffect(() => {
    if (integratedSystemId) {
      // First filter by integrated system
      let filteredProjects = props.dataProjects.filter(
        (dataProject) =>
          dataProject.integrated_system_id ===
          Number.parseInt(integratedSystemId)
      );

      // Then filter by dataProjectType prop if provided
      if (props.dataProjectType) {
        if (props.dataProjectType === "data_quality") {
          filteredProjects = filteredProjects.filter(
            (dataProject) =>
              dataProject.data_project_types?.includes(props.dataProjectType) ||
              isEmpty(dataProject.data_project_types)
          );
        } else {
          filteredProjects = filteredProjects.filter((dataProject) =>
            dataProject.data_project_types?.includes(props.dataProjectType)
          );
        }
      }

      if (
        props.dataProjectType === "enrich" &&
        parsedEnrichSystemId !== undefined
      ) {
        filteredProjects = filteredProjects.filter(
          (dataProject) => dataProject.enrich_system_id === parsedEnrichSystemId
        );
      }

      const sortedProjects = filteredProjects.sort(
        (first, second) => first.view_order - second.view_order
      );
      setEligibleDataProjects(sortedProjects);

      const existingCategories = [
        ...new Set(
          filteredProjects.map((project) => project.category || "Home")
        ),
      ];
      setAvailableProjectCategories(existingCategories);

      props.setSelectedSystemId(Number.parseInt(integratedSystemId));

      // Initialize category from URL or default to "Home"
      const suppliedCategory = searchParams.get("category");
      if (
        suppliedCategory &&
        existingCategories.indexOf(suppliedCategory) > -1
      ) {
        setSelectedProjectCategory(suppliedCategory);
      } else {
        setSelectedProjectCategory("Home");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    integratedSystemId,
    parsedEnrichSystemId,
    props.dataProjects,
    props.dataProjectType,
  ]);

  // Compute filtered projects based on selected category
  const filteredDataProjects = useMemo(() => {
    return eligibleDataProjects.filter(
      (project) => (project.category || "Home") === selectedProjectCategory
    );
  }, [eligibleDataProjects, selectedProjectCategory]);

  // Sync selected category to URL params
  useEffect(() => {
    if (selectedProjectCategory) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("category", selectedProjectCategory);
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedProjectCategory, setSearchParams]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchProjectTickets = async () => {
      let allTickets: DataProjectTicketCount[] = [];
      let nextOffset = 0;

      try {
        while (nextOffset !== null) {
          if (abortController.signal.aborted) break;

          const result = await getDataProjectTicketCounts(
            Number.parseInt(integratedSystemId),
            nextOffset
          );

          allTickets = [...allTickets, ...result.res];
          nextOffset = result.next_off_set;
          setDataProjectTicketCounts(allTickets);
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error("Failed to fetch ticket counts:", err);
        }
      }
    };

    if (integratedSystemId) {
      fetchProjectTickets();
    }

    return () => abortController.abort();
  }, [integratedSystemId]);

  return (
    <React.Fragment>
      <Helmet title="Data Projects | CIRROM" />

      {/* Header */}
      <Grid container justifyContent="space-between" spacing={6}>
        <Grid>
          <Typography variant="h3" gutterBottom>
            Data Projects
          </Typography>
          {selectedEnrichSystemName && (
            <Typography variant="subtitle1" color="textSecondary">
              Enrich Source: {selectedEnrichSystemName}
            </Typography>
          )}
        </Grid>
        <Grid>
          {availableProjectCategories && availableProjectCategories.length > 0 && (
            <TextField
              select
              value={selectedProjectCategory}
              size="small"
              InputProps={{
                style: {
                  height: "35px",
                  width: "150px",
                  fontSize: "14px",
                },
              }}
              onChange={(event) =>
                setSelectedProjectCategory(event.target.value)
              }
            >
              {availableProjectCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Grid>
      </Grid>
      <Divider my={6} />

      {/* Edit View */}
      {showEditView && eligibleDataProjects && (
        <DraggableProjectList
          dataProjects={eligibleDataProjects}
          onCancel={() => setShowEditView(false)}
        />
      )}

      {/* Grid View */}
      {!showEditView && (
        <>
          <Grid container spacing={3}>
            {/* Add New Project Card */}
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 3,
              }}
            >
              <AddNewDataProject
                systemId={Number.parseInt(integratedSystemId)}
                onClose={() => props.fetchDataProjects()}
                dataProjectType={props.dataProjectType}
                lockedEnrichSystemId={parsedEnrichSystemId}
              />
            </Grid>

            {/* Data Project Cards */}
            {filteredDataProjects.map((project) => (
              <Grid
                key={project.id}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                  lg: 3,
                  xl: 3,
                }}
              >
                <DataProjectPanel
                  dataProject={project}
                  dataProjectTicketCounts={dataProjectTicketCounts.filter(
                    (item) => item.data_project_id === project.id
                  )}
                  onRefresh={() => props.fetchDataProjects()}
                  dataProjectType={props.dataProjectType}
                />
              </Grid>
            ))}
          </Grid>

          {/* Edit Button */}
          <div
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              marginTop: "20px",
              marginRight: "20px",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ textTransform: "capitalize" }}
              onClick={() => setShowEditView(!showEditView)}
            >
              Edit
            </Button>
          </div>
        </>
      )}
    </React.Fragment>
  );
});
